// apps/office/src/pages/office/InteriorClientProjects.jsx
import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  Home,
  MapPin,
  Calendar,
  Palette,
  Wallet,
  Receipt,
  FileText,
  TrendingUp,
  TrendingDown,
  Loader2,
  ArrowLeft,
  Phone,
  Mail,
  Pencil,
  Trash2,
  DollarSign,
  PieChart,
  User,
  RefreshCw,
  BarChart3,
  Check,
  X,
  IndianRupee,
  ArrowDownRight,
  ArrowUpRight,
  CircleDollarSign,
  AlertCircle,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import pb from '../../lib/pocketbaseClient';
import { useAuth } from '../../contexts/AuthContext';
import InteriorProjectForm from '../../components/office/InteriorProjectForm';

const rs = (v) => {
  const n = Number(v) || 0;
  return `Rs. ${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(n)}`;
};

const fmtDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return '—'; }
};

const STATUS_COLORS = {
  Draft: 'bg-slate-100 text-slate-700 border-slate-200',
  Active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'On Hold': 'bg-amber-100 text-amber-700 border-amber-200',
  Completed: 'bg-blue-100 text-blue-700 border-blue-200',
  Cancelled: 'bg-red-100 text-red-700 border-red-200',
};

const InteriorClientProjects = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [formOpen, setFormOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [statusChangeProject, setStatusChangeProject] = useState(null);
  const [analysisProject, setAnalysisProject] = useState(null);

  const isAdmin = ['admin', 'super_admin'].includes(currentUser?.role);

  /* ── Fetch client ── */
  const { data: client, isLoading: clientLoading } = useQuery({
    queryKey: ['interior-client', clientId],
    queryFn: () => pb.collection('leads').getOne(clientId),
    enabled: !!clientId,
  });

  /* ── Fetch this client's projects ── */
  const { data: projects = [], isLoading: projectsLoading, refetch } = useQuery({
    queryKey: ['interior-projects', clientId],
    queryFn: () =>
      pb.collection('interior_projects').getFullList({
        sort: '-created',
        filter: `clientId = "${clientId}"`,
      }),
    enabled: !!clientId,
  });

  /* ── Delete ── */
  /* ── Fetch approved quotations for this client's projects ── */
  const projectIds = useMemo(() => projects.map(p => p.id), [projects]);

  const { data: approvedQuotations = [] } = useQuery({
    queryKey: ['interior-quotations-approved', clientId, projectIds.join(',')],
    queryFn: async () => {
      if (projectIds.length === 0) return [];
      const filter = `isLatest = true && status = "Approved" && (${projectIds.map(id => `projectId = "${id}"`).join(' || ')})`;
      return await pb.collection('interior_quotations').getFullList({
        filter,
        fields: 'id,projectId,total,quoteNumber',
      });
    },
    enabled: projectIds.length > 0,
  });

  /* ── Aggregate approved quote sum per project ── */
  const projectQuoteSums = useMemo(() => {
    const sums = {};
    approvedQuotations.forEach(q => {
      if (q.projectId) {
        sums[q.projectId] = (sums[q.projectId] || 0) + (Number(q.total) || 0);
      }
    });
    return sums;
  }, [approvedQuotations]);

  const deleteProject = useMutation({
    mutationFn: (id) => pb.collection('interior_projects').delete(id),
    onSuccess: () => {
      toast.success('Project deleted');
      queryClient.invalidateQueries(['interior-projects']);
      refetch();
    },
    onError: (err) => toast.error('Delete failed: ' + err.message),
    onSettled: () => setDeletingId(null),
  });

  const filtered = useMemo(() => {
    const s = search.toLowerCase();
    return projects.filter((p) => {
      if (statusFilter !== 'all' && p.status !== statusFilter) return false;
      if (!s) return true;
      return (
        p.propertyName?.toLowerCase().includes(s) ||
        p.propertyAddress?.toLowerCase().includes(s)
      );
    });
  }, [projects, search, statusFilter]);


  /* ─── Quick Status Update ─── */
  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }) =>
      pb.collection('interior_projects').update(id, { status }),
    onSuccess: (_, vars) => {
      toast.success(`✅ Status updated to ${vars.status}`);
      queryClient.invalidateQueries(['interior-projects']);
      refetch();
      setStatusChangeProject(null);
    },
    onError: (err) => toast.error('Update failed: ' + err.message),
  });

  /* ─── Create Invoice handler ─── */
  const handleCreateInvoice = async (project) => {
    try {
      // Find approved or invoiced interior quotation for this project
      const quotes = await pb.collection('interior_quotations').getFullList({
        filter: `projectId = "${project.id}" && isLatest = true && (status = "Approved" || status = "Invoiced")`,
        sort: '-created',
      });
      if (quotes.length === 0) {
        toast.error('No approved quotation yet. Approve a quotation first to create invoice.');
        return;
      }
      // Check if invoice already exists
      try {
        const existing = await pb.collection('invoices').getFirstListItem(
          `interiorQuotationId = "${quotes[0].id}"`,
          { sort: '-created' }
        );
        if (existing) {
          navigate('/office/invoices');
          return;
        }
      } catch (e) { /* no invoice yet — proceed */ }
      // Navigate to invoice form with the first approved quote
      navigate(`/office/interior-invoices/new/${quotes[0].id}`);
    } catch (e) {
      toast.error('Failed to open invoice form: ' + e.message);
    }
  };

  /* ─── Expenses for analysis (silent fail if collection not set up) ─── */
  const { data: projectExpenses = [] } = useQuery({
    queryKey: ['interior-project-expenses', analysisProject?.id],
    queryFn: async () => {
      if (!analysisProject?.id) return [];
      try {
        return await pb.collection('interior_project_expenses').getFullList({
          filter: `project = "${analysisProject.id}"`,
        });
      } catch {
        // Try fallback collection name
        try {
          return await pb.collection('project_expenses').getFullList({
            filter: `project = "${analysisProject.id}"`,
          });
        } catch {
          return [];
        }
      }
    },
    enabled: !!analysisProject?.id,
  });

  const openNewForm = () => {
    setEditingProject(null);
    setFormOpen(true);
  };

  const openEditForm = (project) => {
    setEditingProject(project);
    setFormOpen(true);
  };

  if (clientLoading || projectsLoading) {
    return (
      <div className="flex justify-center items-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Back button + Client header */}
      <div className="flex items-center gap-3">
        <Button
          variant="outline"
          size="icon"
          onClick={() => navigate('/office/interior-projects')}
          className="shrink-0"
        >
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800 truncate">
            {client?.name || 'Client Projects'}
          </h1>
          {client?.jobId && (
            <p className="text-xs text-slate-500 font-mono mt-0.5">{client.jobId}</p>
          )}
        </div>
        <Button
          onClick={openNewForm}
          className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2 shrink-0"
        >
          <Plus className="w-4 h-4" /> Add Project
        </Button>
      </div>

      {/* Client info strip */}
      {client && (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex flex-wrap gap-4 items-center">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white font-bold text-lg shrink-0">
              {(client.name || '?').charAt(0).toUpperCase()}
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-600 flex-1 min-w-0">
              {client.phone && (
                <span className="flex items-center gap-1.5">
                  <Phone className="w-3 h-3 text-slate-400" /> {client.phone}
                </span>
              )}
              {client.email && (
                <span className="flex items-center gap-1.5">
                  <Mail className="w-3 h-3 text-slate-400" /> {client.email}
                </span>
              )}
              {client.propertyType && (
                <span className="flex items-center gap-1.5">
                  <Palette className="w-3 h-3 text-slate-400" /> {client.propertyType}
                </span>
              )}
              {client.address && (
                <span className="flex items-center gap-1.5">
                  <MapPin className="w-3 h-3 text-slate-400" /> {client.address}
                </span>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search + Status filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by property name or address…"
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {['all', 'Draft', 'Active', 'On Hold', 'Completed', 'Cancelled'].map((s) => (
            <Button
              key={s}
              variant={statusFilter === s ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter(s)}
              className="capitalize"
            >
              {s}
            </Button>
          ))}
        </div>
      </div>

      {/* Projects */}
      {filtered.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-20 text-center">
            <Home className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500 font-medium">
              {projects.length === 0 ? 'No projects yet' : 'No projects match your filters'}
            </p>
            <p className="text-xs text-slate-400 mt-1">
              {projects.length === 0
                ? `Start by adding a project for ${client?.name || 'this client'}`
                : 'Try a different search'}
            </p>
            {projects.length === 0 && (
              <Button
                onClick={openNewForm}
                className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
              >
                <Plus className="w-4 h-4" /> Add First Project
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((project) => {
            const isDeleting = deletingId === project.id;

            return (
              <Card
                key={project.id}
                className="border-0 shadow-md hover:shadow-xl transition-shadow overflow-hidden"
              >
                <div className="h-1.5 bg-gradient-to-r from-emerald-500 to-teal-500" />

                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="min-w-0 flex-1">
                      <h3 className="text-base font-bold text-slate-800 truncate" title={project.propertyName}>
                        {project.propertyName || 'Untitled Project'}
                      </h3>
                    </div>
                    <Badge className={STATUS_COLORS[project.status] || STATUS_COLORS.Draft}>
                      {project.status || 'Draft'}
                    </Badge>
                  </div>

                  {project.propertyAddress && (
                    <div className="flex items-start gap-1.5 text-xs text-slate-600 mb-3">
                      <MapPin className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
                      <span className="line-clamp-2">{project.propertyAddress}</span>
                    </div>
                  )}

                  <div className="space-y-1.5 mb-3">
                    <div className="flex items-center gap-1.5 text-xs text-slate-600">
                      <Calendar className="w-3.5 h-3.5 text-slate-400" />
                      <span>
                        {fmtDate(project.workStartDate)} → {fmtDate(project.workEndDate)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-slate-600">
                      <Palette className="w-3.5 h-3.5 text-slate-400" />
                      <span>{project.designStyle || 'Standard Style'}</span>
                    </div>
                  </div>

                  {projectQuoteSums[project.id] > 0 && (
                    <div className="mt-3 p-3 rounded-lg bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <IndianRupee className="w-3.5 h-3.5 text-emerald-700" />
                          <span className="text-[10px] uppercase text-emerald-700 tracking-wide font-bold">
                            Approved Quote
                          </span>
                        </div>
                        <span className="text-sm font-bold text-emerald-700">
                          {rs(projectQuoteSums[project.id])}
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-2 pt-3 border-t border-slate-100">
                    <Button
                      size="sm" variant="outline"
                      onClick={() => navigate(`/office/payments-dashboard?client=${clientId}`)}
                      className="gap-1.5 text-xs border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                    >
                      <Wallet className="w-3.5 h-3.5" /> Payment
                    </Button>
                    <Button
                      size="sm" variant="outline"
                      onClick={() => toast.info('Expense — coming soon')}
                      className="gap-1.5 text-xs border-amber-200 text-amber-700 hover:bg-amber-50"
                    >
                      <Receipt className="w-3.5 h-3.5" /> Expense
                    </Button>
                    <Button
                      size="sm" variant="outline"
                      onClick={() => navigate(`/office/interior-quotations/project/${project.id}`)}
                      className="gap-1.5 text-xs border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                    >
                      <FileText className="w-3.5 h-3.5" /> Quotation
                    </Button>
                    <Button
                      size="sm" variant="outline"
                      onClick={() => handleCreateInvoice(project)}
                      className="gap-1.5 text-xs border-cyan-200 text-cyan-700 hover:bg-cyan-50"
                    >
                      <PieChart className="w-3.5 h-3.5" /> Invoice
                    </Button>
                    <Button
                      size="sm" variant="outline"
                      onClick={() => setStatusChangeProject(project)}
                      className="gap-1.5 text-xs border-purple-200 text-purple-700 hover:bg-purple-50"
                    >
                      <RefreshCw className="w-3.5 h-3.5" /> Status
                    </Button>
                    <Button
                      size="sm" variant="outline"
                      onClick={() => setAnalysisProject(project)}
                      className="gap-1.5 text-xs border-rose-200 text-rose-700 hover:bg-rose-50"
                    >
                      <BarChart3 className="w-3.5 h-3.5" /> Analysis
                    </Button>
                  </div>

                  {isAdmin && (
                    <div className="flex justify-end gap-1 mt-2 pt-2 border-t border-slate-100">
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => openEditForm(project)}
                        className="h-7 px-2 text-slate-500 hover:text-slate-700"
                        title="Edit"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        size="sm" variant="ghost"
                        disabled={isDeleting}
                        onClick={() => {
                          if (window.confirm(`Delete "${project.propertyName}"? This cannot be undone.`)) {
                            setDeletingId(project.id);
                            deleteProject.mutate(project.id);
                          }
                        }}
                        className="h-7 px-2 text-red-400 hover:text-red-600"
                        title="Delete"
                      >
                        {isDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Form Dialog */}
      <InteriorProjectForm
        open={formOpen}
        onOpenChange={(v) => {
          setFormOpen(v);
          if (!v) setEditingProject(null);
        }}
        existingProject={editingProject}
        presetClientId={clientId}
        onSuccess={() => {
          queryClient.invalidateQueries(['interior-projects']);
          refetch();
        }}
      />

      {/* ═══════════════════════════════════════════════════
          Status Change Dialog
          ═══════════════════════════════════════════════════ */}
      {statusChangeProject && (
        <Dialog
          open={!!statusChangeProject}
          onOpenChange={(v) => !v && setStatusChangeProject(null)}
        >
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <RefreshCw className="w-5 h-5 text-purple-600" />
                Change Status
              </DialogTitle>
            </DialogHeader>

            <p className="text-xs text-slate-500 -mt-2">
              {statusChangeProject.propertyName}
            </p>

            <div className="space-y-2 py-2">
              {[
                { value: 'Draft', color: 'slate' },
                { value: 'Active', color: 'emerald' },
                { value: 'On Hold', color: 'amber' },
                { value: 'Completed', color: 'blue' },
                { value: 'Cancelled', color: 'red' },
              ].map(({ value }) => {
                const isCurrent = statusChangeProject.status === value;
                return (
                  <button
                    key={value}
                    onClick={() => {
                      if (isCurrent) {
                        setStatusChangeProject(null);
                        return;
                      }
                      updateStatusMutation.mutate({
                        id: statusChangeProject.id,
                        status: value,
                      });
                    }}
                    disabled={updateStatusMutation.isPending}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border-2 transition-all text-sm font-medium ${
                      isCurrent
                        ? 'bg-emerald-50 border-emerald-400 text-emerald-700 shadow-sm'
                        : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      {value === 'Active' && <TrendingUp className="w-4 h-4 text-emerald-600" />}
                      {value === 'On Hold' && <AlertCircle className="w-4 h-4 text-amber-600" />}
                      {value === 'Completed' && <CheckCircle className="w-4 h-4 text-blue-600" />}
                      {value === 'Draft' && <FileText className="w-4 h-4 text-slate-500" />}
                      {value === 'Cancelled' && <XCircle className="w-4 h-4 text-red-600" />}
                      {value}
                    </span>
                    {isCurrent && (
                      <span className="text-[10px] uppercase tracking-wider text-emerald-600 font-bold">
                        Current
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setStatusChangeProject(null)}
                disabled={updateStatusMutation.isPending}
              >
                Cancel
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ═══════════════════════════════════════════════════
          Analysis Dialog
          ═══════════════════════════════════════════════════ */}
      {analysisProject && (
        <Dialog
          open={!!analysisProject}
          onOpenChange={(v) => !v && setAnalysisProject(null)}
        >
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-rose-600" />
                Project Analysis
              </DialogTitle>
            </DialogHeader>

            <p className="text-xs text-slate-500 -mt-2 mb-2">
              {analysisProject.propertyName}
            </p>

            {(() => {
              const quote = Number(analysisProject.quotationAmount) || 0;
              const paid = Number(analysisProject.paidAmount) || 0;
              const expenses = projectExpenses.reduce(
                (s, e) => s + (Number(e.amount) || 0),
                0
              );
              const due = Math.max(0, quote - paid);
              const netProfit = paid - expenses;
              const expectedProfit = quote - expenses;
              const margin = quote > 0 ? (expectedProfit / quote) * 100 : 0;

              const totalExpensesByCategory = {};
              projectExpenses.forEach((e) => {
                const cat = e.category || 'other';
                totalExpensesByCategory[cat] =
                  (totalExpensesByCategory[cat] || 0) + (Number(e.amount) || 0);
              });

              return (
                <>
                  {/* Main financial grid */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                      <div className="flex items-center gap-1.5 mb-1">
                        <FileText className="w-3.5 h-3.5 text-indigo-600" />
                        <p className="text-[10px] uppercase text-indigo-700 tracking-wide font-bold">
                          Quote Value
                        </p>
                      </div>
                      <p className="text-lg font-bold text-indigo-700">{rs(quote)}</p>
                    </div>

                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                      <div className="flex items-center gap-1.5 mb-1">
                        <ArrowUpRight className="w-3.5 h-3.5 text-emerald-600" />
                        <p className="text-[10px] uppercase text-emerald-700 tracking-wide font-bold">
                          Received
                        </p>
                      </div>
                      <p className="text-lg font-bold text-emerald-700">{rs(paid)}</p>
                    </div>

                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <div className="flex items-center gap-1.5 mb-1">
                        <ArrowDownRight className="w-3.5 h-3.5 text-amber-600" />
                        <p className="text-[10px] uppercase text-amber-700 tracking-wide font-bold">
                          Expenses
                        </p>
                      </div>
                      <p className="text-lg font-bold text-amber-700">{rs(expenses)}</p>
                    </div>

                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <div className="flex items-center gap-1.5 mb-1">
                        <AlertCircle className="w-3.5 h-3.5 text-red-600" />
                        <p className="text-[10px] uppercase text-red-700 tracking-wide font-bold">
                          Due
                        </p>
                      </div>
                      <p className="text-lg font-bold text-red-700">{rs(due)}</p>
                    </div>
                  </div>

                  {/* Profit analysis */}
                  <div className="border-t border-slate-200 pt-3 space-y-2 mb-3">
                    <p className="text-[10px] uppercase text-slate-500 tracking-wide font-bold mb-2">
                      Profit Analysis
                    </p>

                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-1.5 text-slate-600">
                        <CircleDollarSign className="w-3.5 h-3.5" />
                        Net Cash Profit
                        <span className="text-[10px] text-slate-400">(received − expenses)</span>
                      </span>
                      <span className={`font-bold ${netProfit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {netProfit >= 0 ? '+' : ''}{rs(netProfit)}
                      </span>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-1.5 text-slate-600">
                        <TrendingUp className="w-3.5 h-3.5" />
                        Expected Profit
                        <span className="text-[10px] text-slate-400">(quote − expenses)</span>
                      </span>
                      <span className={`font-bold ${expectedProfit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {expectedProfit >= 0 ? '+' : ''}{rs(expectedProfit)}
                      </span>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">Profit Margin</span>
                      <span className={`font-bold ${margin >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {margin.toFixed(1)}%
                      </span>
                    </div>
                  </div>

                  {/* Expenses breakdown */}
                  {projectExpenses.length > 0 && (
                    <div className="border-t border-slate-200 pt-3">
                      <p className="text-[10px] uppercase text-slate-500 tracking-wide font-bold mb-2">
                        Expenses Breakdown ({projectExpenses.length})
                      </p>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {Object.entries(totalExpensesByCategory).map(([cat, amt]) => (
                          <div
                            key={cat}
                            className="flex items-center justify-between text-xs bg-slate-50 px-2 py-1.5 rounded"
                          >
                            <span className="capitalize text-slate-600">{cat}</span>
                            <span className="font-semibold text-slate-700">{rs(amt)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {projectExpenses.length === 0 && (
                    <div className="border-t border-slate-200 pt-3 text-center">
                      <p className="text-xs text-slate-400">
                        No expenses recorded yet
                      </p>
                    </div>
                  )}
                </>
              );
            })()}

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setAnalysisProject(null)}
              >
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default InteriorClientProjects;
